import { useState, type FormEvent, type ReactNode } from 'react';

import type { BankAccount, OfferedMethod, OfferedMethodId } from '../api/endpoints/planPayment';
import { METHOD_COPY, payButtonLabel } from '../lib/planPayment';
import { formatWholeDollars } from './PlanPicker';

/**
 * The ways to pay, as one choice: a row per configured method, the chosen one
 * opened in place. Stripe (first when it is on), card and PayPal leave for
 * the provider's own page; Yappy and transfer show Dale's details and take
 * the operation number back.
 */

const REFERENCE_MIN = 3;
const REFERENCE_MESSAGE = 'Escribe el número de operación de tu pago.';
const REPORT_LABEL: Record<'yappy' | 'transferencia', string> = {
  yappy: 'Ya pagué, reportar pago',
  transferencia: 'Ya transferí, reportar pago',
};
const HOSTED_LEAD: Record<'stripe' | 'tarjeta' | 'paypal', string> = {
  stripe: 'Te llevamos a Stripe para agregar tu tarjeta. Se cobra hoy y luego se renueva solo. Al terminar vuelves aquí.',
  tarjeta: 'Te llevamos a PagueloFácil para pagar con tu tarjeta. Al terminar vuelves aquí.',
  paypal: 'Te llevamos a PayPal para pagar. Al terminar vuelves aquí.',
};

export type PagoMethodsProps = {
  methods: OfferedMethod[];
  amountCents: number;
  busy: boolean;
  onHosted: (method: 'tarjeta' | 'paypal') => void;
  onReport: (method: 'yappy' | 'transferencia', reference: string) => void;
  /** Stripe Checkout for the plan on file (`startStripeCheckout`); only called when Stripe is offered. */
  onStripe?: () => void;
};

export function PagoMethods({ methods, amountCents, busy, onHosted, onReport, onStripe }: PagoMethodsProps) {
  const [chosen, setChosen] = useState<OfferedMethodId>(methods[0]!.id);
  return (
    <fieldset className="pago-methods">
      <legend className="section-label">Cómo quieres pagar</legend>
      {methods.map((method) => (
        <MethodRow key={method.id} method={method.id} chosen={chosen === method.id} onChoose={() => setChosen(method.id)}>
          {chosen === method.id ? (
            <MethodDetail method={method} amountCents={amountCents} busy={busy} onHosted={onHosted} onReport={onReport} {...(onStripe ? { onStripe } : {})} />
          ) : null}
        </MethodRow>
      ))}
    </fieldset>
  );
}

function MethodRow({ method, chosen, onChoose, children }: { method: OfferedMethodId; chosen: boolean; onChoose: () => void; children: ReactNode }) {
  const copy = METHOD_COPY[method];
  return (
    <div className="pago-method" data-chosen={chosen ? 'true' : undefined}>
      <label className="pago-method-head">
        <input type="radio" name="pago-metodo" value={method} checked={chosen} onChange={onChoose} />
        <MethodGlyph method={method} />
        <span className="pago-method-text">
          <span className="pago-method-title">{copy.title}</span>
          <span className="pago-method-sub">{copy.sub}</span>
        </span>
      </label>
      {children}
    </div>
  );
}

type DetailProps = Omit<PagoMethodsProps, 'methods'> & { method: OfferedMethod };

function MethodDetail({ method, amountCents, busy, onHosted, onReport, onStripe }: DetailProps) {
  if (method.id === 'stripe' || method.id === 'tarjeta' || method.id === 'paypal') {
    const id = method.id;
    const start = () => (id === 'stripe' ? onStripe?.() : onHosted(id));
    return (
      <div className="pago-method-body">
        <p className="pago-method-lead">{HOSTED_LEAD[id]}</p>
        <button type="button" className="btn" disabled={busy} onClick={start}>
          {busy ? 'Abriendo…' : payButtonLabel(id, amountCents)}
        </button>
        <p className="pago-trust">
          <LockGlyph />
          Dale no ve ni guarda los datos de tu tarjeta.
        </p>
      </div>
    );
  }
  return (
    <div className="pago-method-body">
      {method.id === 'yappy' ? (
        <YappyDetails holder={method.holder} directory={method.directory} phone={method.phone} amountCents={amountCents} />
      ) : (
        <TransferDetails accounts={method.accounts} amountCents={amountCents} />
      )}
      <ReportForm method={method.id} busy={busy} onReport={onReport} />
    </div>
  );
}

function YappyDetails({ holder, directory, phone, amountCents }: { holder: string; directory?: string; phone?: string; amountCents: number }) {
  return (
    <dl className="pago-details">
      {directory ? <DetailRow label="En el directorio de Yappy" value={directory} /> : null}
      {phone ? <DetailRow label="Número" value={phone} copy /> : null}
      <DetailRow label="A nombre de" value={holder} />
      <DetailRow label="Monto exacto" value={formatWholeDollars(amountCents)} />
    </dl>
  );
}

function TransferDetails({ accounts, amountCents }: { accounts: BankAccount[]; amountCents: number }) {
  return (
    <>
      {accounts.map((account) => (
        <dl className="pago-details" key={`${account.bank}-${account.accountNumber}`}>
          <DetailRow label="Banco" value={account.bank} />
          <DetailRow label="A nombre de" value={account.holder} />
          <DetailRow label="Tipo de cuenta" value={account.accountType} />
          <DetailRow label="Número de cuenta" value={account.accountNumber} copy />
          {account.taxId ? <DetailRow label="RUC" value={account.taxId} /> : null}
          {account.note ? <DetailRow label="Nota" value={account.note} /> : null}
        </dl>
      ))}
      <dl className="pago-details">
        <DetailRow label="Monto exacto" value={formatWholeDollars(amountCents)} />
      </dl>
    </>
  );
}

function DetailRow({ label, value, copy = false }: { label: string; value: string; copy?: boolean }) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard can be blocked; the number stays on screen to copy by hand.
      setCopied(false);
    }
  }
  return (
    <div>
      <dt>{label}</dt>
      <dd>
        <span>{value}</span>
        {copy ? (
          <button type="button" className="pago-copy" onClick={() => void handleCopy()}>
            {copied ? 'Copiado' : 'Copiar'}
          </button>
        ) : null}
      </dd>
    </div>
  );
}

function ReportForm({ method, busy, onReport }: { method: 'yappy' | 'transferencia'; busy: boolean; onReport: PagoMethodsProps['onReport'] }) {
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | undefined>();
  const inputId = `pago-referencia-${method}`;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = reference.trim();
    if (trimmed.length < REFERENCE_MIN) {
      setError(REFERENCE_MESSAGE);
      return;
    }
    setError(undefined);
    onReport(method, trimmed);
  }

  return (
    <form className="pago-report" onSubmit={handleSubmit} noValidate>
      <label className="pago-report-label" htmlFor={inputId}>
        Número de operación o referencia
      </label>
      <input
        id={inputId}
        type="text"
        value={reference}
        autoComplete="off"
        spellCheck={false}
        maxLength={60}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${inputId}-error` : undefined}
        onChange={(event) => setReference(event.target.value)}
      />
      {error ? (
        <p className="pago-report-error" id={`${inputId}-error`}>
          {error}
        </p>
      ) : null}
      <button type="submit" className="btn" disabled={busy}>
        {busy ? 'Enviando…' : REPORT_LABEL[method]}
      </button>
    </form>
  );
}

function MethodGlyph({ method }: { method: OfferedMethodId }) {
  return (
    <span className="pago-method-glyph" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        {GLYPH_PATHS[method]}
      </svg>
    </span>
  );
}

const CARD_GLYPH = (
  <>
    <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
    <path d="M3 10h18M7 15h3" />
  </>
);

const GLYPH_PATHS: Record<OfferedMethodId, ReactNode> = {
  stripe: CARD_GLYPH,
  tarjeta: CARD_GLYPH,
  paypal: (
    <>
      <path d="M5 8.5A2.5 2.5 0 0 1 7.5 6H18a1 1 0 0 1 1 1v2.5" />
      <path d="M5 8.5V17a2 2 0 0 0 2 2h12a1 1 0 0 0 1-1v-8H7.5A2.5 2.5 0 0 1 5 8.5Z" />
      <circle cx="16" cy="14.5" r="1" />
    </>
  ),
  yappy: (
    <>
      <rect x="7" y="3" width="10" height="18" rx="2.5" />
      <path d="M11 18h2" />
    </>
  ),
  transferencia: (
    <>
      <path d="M4 9.5 12 5l8 4.5" />
      <path d="M6 10v7M10 10v7M14 10v7M18 10v7M4 19.5h16" />
    </>
  ),
};

export function LockGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </svg>
  );
}
